// A report's filters, as the filter panel edits them and the address keeps
// them, and how they become the request body.
//
//   {
//     species:  ["Culex pipiens", ...]           basic search: any of these
//     dates:    {field: "report_date" | "imaging_date_time", from, to}
//                                                 calendar days, YYYY-MM-DD
//     excludeEmpty, excludeUnknown                 the two exclusions
//     advanced: {op: "and" | "or", clauses: [...]} | null
//               clauses are {field, operator, value} or one level of groups
//   }
//
// Every report reads the same body: {timezone, excludeEmpty, excludeUnknown,
// criteria}, where criteria ANDs the basic search with the advanced group
// (see services/organization reporting/filter.ts for the grammar).

export const DATE_FIELDS = [
  { key: "report_date", label: "Collection date" },
  { key: "imaging_date_time", label: "Imaging date" },
];

export const DEFAULT_FILTERS = Object.freeze({
  species: [],
  dates: { field: "report_date", from: null, to: null },
  excludeEmpty: true,
  excludeUnknown: false,
  advanced: null,
});

const DAY = /^\d{4}-\d{2}-\d{2}$/;

const shiftDay = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

// a clause the server will accept: a field, an operator, and a value where
// the operator needs one
const NO_VALUE = ["is_empty", "is_not_empty"];
const LIST = ["any_of", "none_of", "between"];
export const isComplete = (c) => {
  if (!c?.field || !c?.operator) return false;
  if (NO_VALUE.includes(c.operator)) return true;
  if (c.operator === "between") return Array.isArray(c.value) && c.value.length === 2 && c.value.every((v) => v !== "" && v != null);
  if (LIST.includes(c.operator)) return Array.isArray(c.value) && c.value.length > 0;
  return c.value !== undefined && c.value !== null && c.value !== "";
};

const isGroup = (node) => node && Array.isArray(node.clauses);

/** The advanced group with incomplete clauses and empty groups dropped. */
export const completeAdvanced = (advanced) => {
  if (!isGroup(advanced)) return null;
  const clauses = advanced.clauses
    .map((c) => (isGroup(c) ? completeAdvanced(c) : isComplete(c) ? { field: c.field, operator: c.operator, ...(NO_VALUE.includes(c.operator) ? {} : { value: c.value }) } : null))
    .filter(Boolean);
  return clauses.length ? { op: advanced.op === "or" ? "or" : "and", clauses } : null;
};

/** The criteria tree for the request, or null when nothing is set. */
export const toCriteria = (filters) => {
  const clauses = [];
  if (filters.species?.length) {
    clauses.push({ field: "summary_species", operator: "any_of", value: filters.species });
  }
  const { field, from, to } = filters.dates ?? {};
  if (from && to) clauses.push({ field, operator: "between", value: [from, to] });
  // "after" and "before" leave out the day named, so step one day out
  else if (from) clauses.push({ field, operator: "after", value: shiftDay(from, -1) });
  else if (to) clauses.push({ field, operator: "before", value: shiftDay(to, 1) });
  const advanced = completeAdvanced(filters.advanced);
  if (advanced) clauses.push(advanced);
  return clauses.length ? { op: "and", clauses } : null;
};

export const toRequestFilter = (filters, timezone) => {
  const criteria = toCriteria(filters);
  return {
    timezone,
    excludeEmpty: Boolean(filters.excludeEmpty),
    excludeUnknown: Boolean(filters.excludeUnknown),
    ...(criteria ? { criteria } : {}),
  };
};

/** How many filters are set: species, dates, and advanced conditions. */
export const activeFilterCount = (filters) => {
  const count = (node) => (isGroup(node) ? node.clauses.reduce((n, c) => n + count(c), 0) : 1);
  const advanced = completeAdvanced(filters.advanced);
  return (
    (filters.species?.length ? 1 : 0) +
    (filters.dates?.from || filters.dates?.to ? 1 : 0) +
    (advanced ? count(advanced) : 0)
  );
};

/**
 * Filters read back from an address, kept only where they have the right
 * shape: anything else falls back to the default, so a hand-edited or old
 * link opens a report rather than an error.
 */
export const normalizeFilters = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ...DEFAULT_FILTERS };
  const dates = raw.dates && typeof raw.dates === "object" ? raw.dates : {};
  const day = (v) => (typeof v === "string" && DAY.test(v) ? v : null);
  return {
    species: Array.isArray(raw.species) ? raw.species.filter((s) => typeof s === "string") : [],
    dates: {
      field: DATE_FIELDS.some((f) => f.key === dates.field) ? dates.field : "report_date",
      from: day(dates.from),
      to: day(dates.to),
    },
    excludeEmpty: raw.excludeEmpty === undefined ? DEFAULT_FILTERS.excludeEmpty : Boolean(raw.excludeEmpty),
    excludeUnknown: Boolean(raw.excludeUnknown),
    advanced: isGroup(raw.advanced) ? raw.advanced : null,
  };
};

const dayLabel = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
const fmt = (iso) => dayLabel.format(new Date(`${iso}T00:00:00Z`));

/** "Collected Jul 1, 2026 - Sep 30, 2026", "Imaged from Jun 2, 2026", "All dates". */
export const describeDates = (filters) => {
  const { field, from, to } = filters?.dates ?? {};
  const verb = field === "imaging_date_time" ? "Imaged" : "Collected";
  if (from && to) return `${verb} ${fmt(from)} - ${fmt(to)}`;
  if (from) return `${verb} from ${fmt(from)}`;
  if (to) return `${verb} through ${fmt(to)}`;
  return "All dates";
};

// ---- date presets -----------------------------------------------------------

const isoOf = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** The presets offered next to the date pickers, as {from, to} given today. */
export const DATE_PRESETS = [
  { key: "last7", label: "Last 7 days", range: (today) => ({ from: isoOf(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6)), to: isoOf(today) }) },
  { key: "last30", label: "Last 30 days", range: (today) => ({ from: isoOf(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29)), to: isoOf(today) }) },
  { key: "last90", label: "Last 90 days", range: (today) => ({ from: isoOf(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 89)), to: isoOf(today) }) },
  { key: "thisYear", label: "This year", range: (today) => ({ from: `${today.getFullYear()}-01-01`, to: isoOf(today) }) },
  { key: "lastYear", label: "Last year", range: (today) => ({ from: `${today.getFullYear() - 1}-01-01`, to: `${today.getFullYear() - 1}-12-31` }) },
  // the first specimen's day comes from the server
  { key: "allTime", label: "All time", range: (today, firstDate) => ({ from: firstDate ?? null, to: isoOf(today) }) },
];

export { isoOf };
